import { RICH_TEXT_CHARACTER_LIMIT } from '../../../../config/consts';

const validatorCache = new Map();

export const composeValidators =
  (...validators: ((value: any) => string | undefined)[]) =>
  (value: any): string | undefined => {
    for (const validator of validators) {
      const error = validator(value);
      if (error) {
        return error;
      }
    }
    return undefined;
  };

export const maxLengthRichTextEditor = (value: string, maxLengthMessage: string): string | undefined => {
  if (
    value !== undefined &&
    value !== '' &&
    value !== null &&
    value.replace(/<[^>]+>/g, '').length <= RICH_TEXT_CHARACTER_LIMIT
  ) {
    return undefined;
  }
  return maxLengthMessage;
};

export const requiredValidator = (value: string | number | boolean): string | undefined => {
  if (
    value !== undefined &&
    value !== '' &&
    value !== null &&
    value !== false &&
    (Array.isArray(value) ? value.length !== 0 : true)
  ) {
    return undefined;
  }
  return 'This field is required';
};

export const patternValidator =
  (regex: string) =>
  (value: string | number | boolean): string | undefined => {
    if (value === undefined || value === '' || value === null) {
      return undefined;
    }

    try {
      const regexPattern = new RegExp(regex);
      if (regexPattern.test(String(value))) {
        return undefined;
      }
      return 'Value does not match the required pattern';
    } catch {
      return 'Invalid regex pattern';
    }
  };

export const minMaxValidator =
  ({ min, max }: { min?: number; max?: number }) =>
  (value: string | number): string | undefined => {
    if (value === undefined || value === null || value === '') {
      return undefined;
    }

    if (typeof value === 'string') {
      const length = value.length;

      if (min !== undefined && length < min) {
        return `This field must have at least ${min} character${min !== 1 ? 's' : ''}`;
      }

      if (max !== undefined && length > max) {
        return `This field must have at most ${max} character${max !== 1 ? 's' : ''}`;
      }

      return undefined;
    }

    if (typeof value === 'number') {
      if (min !== undefined && value < min) {
        return `This field must be greater than or equal to ${min}`;
      }

      if (max !== undefined && value > max) {
        return `This field must be less than or equal to ${max}`;
      }

      return undefined;
    }

    return undefined;
  };

export const buildFieldValidators = (field: { required?: boolean; regex?: string; min?: number; max?: number }) => {
  const cacheKey = JSON.stringify({
    required: field.required || false,
    regex: field.regex || '',
    min: field.min,
    max: field.max
  });

  if (validatorCache.has(cacheKey)) {
    return validatorCache.get(cacheKey);
  }

  const validators = [];

  if (field.required) {
    validators.push(requiredValidator);
  }

  if (field.regex) {
    validators.push(patternValidator(field.regex));
  }

  if (field.min !== undefined || field.max !== undefined) {
    validators.push(minMaxValidator(field));
  }

  const composedValidator = validators.length > 0 ? composeValidators(...validators) : undefined;

  validatorCache.set(cacheKey, composedValidator);

  return composedValidator;
};
